// client/src/components/BookingCard.js

import React from 'react';
import { Card, Button, Badge, Image, OverlayTrigger, Tooltip } from 'react-bootstrap';
import { ExclamationTriangleFill, PencilSquare } from 'react-bootstrap-icons';

const getLocalCourseImage = (courseName) => {
  if (!courseName) return;
  const trimmedName = courseName.trim();
  const imageMap = {
    'Lions Municipal Golf Course': '/images/Lions.jpg',
    'Roy Kizer Golf Course': '/images/Kizer.jpg',
    'Jimmy Clay Golf Course': '/images/Clay.jpg',
    'Morris Williams Golf Course': '/images/Morris-Williams.jpg',
  };
  return imageMap[trimmedName];
};

const formatDateTime = (isoString) => {
  if (!isoString) return 'N/A';
  const date = new Date(isoString);
  return date.toLocaleString('en-US', {
    month: '2-digit', day: '2-digit', year: 'numeric',
    hour: 'numeric', minute: '2-digit', hour12: true
  });
};

const BookingCard = ({ booking, onCancel, onAddConfirmation, onEdit }) => {
  const pendingStatuses = ['scheduled', 'active', 'standby_active', 'processing'];
  const isPending = pendingStatuses.includes(booking.status);
  const isPast = !isPending && new Date(booking.start_time) < new Date();
  const isUpcoming = !isPending && !isPast;
  const hasConfirmations = !!booking.confirmation_numbers && booking.confirmation_numbers.length > 0;
  const showNotification = isUpcoming && booking.confirmation_source === 'VermontSystems' && !hasConfirmations;


  const imageUrl = getLocalCourseImage(booking.course_name) || '/images/golf-flag.png';
  // This function provides the correct text and tooltip for pending statuses
  const getPendingStatusInfo = () => {
    if (booking.status === 'standby_active') {
      return {
        text: 'Standby',
        tooltip: 'Will secure tee time as spots that meet your criteria become available.'
      };
    }
    if (booking.status === 'scheduled') {
      return {
        text: 'Scheduled',
        tooltip: 'Will attempt to secure you a tee time once times become available for your booking class.'
      };
    }
    if (booking.status === 'active') {
      return {
        text: 'Searching',
        tooltip: 'The booking window is open — actively searching for a tee time in your requested range.'
      };
    }
    // Fallback for other pending statuses like 'processing'
    return { text: booking.status, tooltip: 'Your booking request is currently being processed.' };
  };

  // This variable now correctly gets its value from the helper function
  const pendingStatus = isPending ? getPendingStatusInfo() : {};
  // ✅ --- END: ADDED HELPER FUNCTION --- ✅

  return (
    <Card className={`mb-3 shadow-sm border-0 ${isPast ? 'bg-light text-muted' : ''}`}>
      <Card.Header className="bg-white d-flex justify-content-between align-items-center">
        <Card.Title className="fw-bold mb-0">{booking.course_name}</Card.Title>

        {/* Show warning icon if numbers are needed */}
        {showNotification && (
          <OverlayTrigger overlay={<Tooltip>Add confirmation numbers</Tooltip>}>
            <ExclamationTriangleFill className="text-warning" size={20} onClick={() => onAddConfirmation(booking)} style={{ cursor: 'pointer' }} />
          </OverlayTrigger>
        )}

        {/* Show edit icon if numbers exist */}
        {hasConfirmations && (
          <OverlayTrigger overlay={<Tooltip>Edit confirmation numbers</Tooltip>}>
            <PencilSquare className="text-secondary" size={20} onClick={() => onAddConfirmation(booking)} style={{ cursor: 'pointer' }} />
          </OverlayTrigger>
        )}
      </Card.Header>

      <Card.Body className="d-flex align-items-center">
        <Image
          src={imageUrl}
          alt={booking.course_name}
          className="rounded flex-shrink-0"
          style={{ width: '60px', height: '60px', objectFit: 'cover', border: '1px solid #eee' }}
        />
        <div className="ms-3 flex-grow-1">
          <div className="d-flex justify-content-between align-items-center mb-1">
            <Card.Subtitle>
              {isUpcoming ? `Receipt #: ${booking.receipt_id || 'N/A'}` : (isPending ? 'Desired Time Window' : '...')}
            </Card.Subtitle>
            {isPending && (
              <OverlayTrigger
                placement="top"
                overlay={<Tooltip id={`tooltip-pending-${booking.id}`}>{pendingStatus.tooltip}</Tooltip>}
              >
                <Badge pill bg="info" text="dark" style={{ cursor: 'pointer' }}>{pendingStatus.text}</Badge>
              </OverlayTrigger>
            )}
            {isUpcoming && <Badge pill bg="success">Confirmed</Badge>}
            {isPast && <Badge bg="secondary">Completed</Badge>}
          </div>
          <Card.Text className="mb-2">
            {formatDateTime(isPending ? booking.start_time_window : booking.start_time)} | {booking.capacity} players
          </Card.Text>
          {isPending && (
            <div>
              <Button variant="light" size="sm" className="me-2 rounded-pill shadow-sm" onClick={() => onEdit(booking)}>Edit Request</Button>
              <Button variant="light" size="sm" className="rounded-pill shadow-sm" onClick={() => onCancel(booking.id)}>Cancel Request</Button>
            </div>
          )}
          {isUpcoming && (
            <Button
              variant="light"
              size="sm"
              className="rounded-pill shadow-sm"
              onClick={() => onCancel(booking)}
            >
              Cancel Booking
            </Button>
          )}
        </div>
      </Card.Body>
    </Card>
  );
};

export default BookingCard;